/**
 * Forge · Fase 1 · Workspace Home (Cognitive OS Home)
 *
 * Non è una dashboard. È il laboratorio di Aria.
 * Layout: 12-col grid dark cosmic.
 * Pannelli = organi cognitivi (non widget):
 *   - Aria Workspaces (sidebar sx)
 *   - Project Universe (centro alto)
 *   - Task Card Recenti (centro basso)
 *   - Aria Panel + Ologramma + Memory Insight (destra)
 *   - Time Machine (footer sx)
 *   - System Metrics (footer dx)
 *
 * Fedele al Manifesto: ogni pannello traduce un punto del Manifesto.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ForgeNavBar } from "../universe/Universe";
import AriaHologram from "../../components/AriaHologram";
import { useWorkspaces, useTasks, apiTask, apiWorkspace } from "./forgeApi";
import "./forge-home.css";

// ── Icone SVG inline (no lucide dip per bundle diet) ──
const Icon = {
  cpu:      (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2"/></svg>,
  brain:    (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><path d="M9 3a3 3 0 0 0-3 3v1a3 3 0 0 0-3 3v2a3 3 0 0 0 2 2.83V17a3 3 0 0 0 3 3h1v-6"/><path d="M15 3a3 3 0 0 1 3 3v1a3 3 0 0 1 3 3v2a3 3 0 0 1-2 2.83V17a3 3 0 0 1-3 3h-1V4z"/></svg>,
  megaphone:(p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><path d="M3 11l14-6v14l-14-6z"/><path d="M17 8v8"/><path d="M7 15v3a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-2"/></svg>,
  layers:   (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><path d="M12 3l9 5-9 5-9-5 9-5z"/><path d="M3 13l9 5 9-5"/><path d="M3 18l9 5 9-5"/></svg>,
  plus:     (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M12 5v14M5 12h14"/></svg>,
  compass:  (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><circle cx="12" cy="12" r="9"/><path d="M15 9l-2 6-5 2 2-6 5-2z"/></svg>,
  terminal: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><path d="M4 6l6 6-6 6"/><path d="M12 20h8"/></svg>,
  bench:    (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><path d="M4 4h16M6 4v16M18 4v16M2 20h20"/></svg>,
  clock:    (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  send:     (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>,
  file:     (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>,
  sparkles: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><path d="M12 3l1.8 4.8L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.2z"/><path d="M5 17l0.9 2.1L8 20l-2.1 0.9L5 23l-0.9-2.1L2 20l2.1-0.9z"/></svg>,
};

// ══════════════════════════════════════════════════════════
// STATUS PILLS
// ══════════════════════════════════════════════════════════
const STATUS = {
  queued:  { label: "In coda",   color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
  running: { label: "In corso",  color: "#fbbf24", bg: "rgba(251,191,36,0.15)"  },
  done:    { label: "Completa",  color: "#22c55e", bg: "rgba(34,197,94,0.15)"   },
  failed:  { label: "Errore",    color: "#f87171", bg: "rgba(248,113,113,0.15)" },
  paused:  { label: "In pausa",  color: "#a78bfa", bg: "rgba(167,139,250,0.15)" },
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.queued;
  return (
    <span className="forge-status-pill"
          style={{ color: s.color, background: s.bg, borderColor: s.color + "40" }}>
      <span className="forge-status-dot" style={{ background: s.color }} />
      {s.label}
    </span>
  );
}

// ══════════════════════════════════════════════════════════
// SIDEBAR — Aria Workspaces
// ══════════════════════════════════════════════════════════
function SidebarWorkspaces({ workspaces, activeId, onSelect, onCreate }) {
  return (
    <aside className="forge-sidebar" data-testid="forge-sidebar">
      <div className="forge-sidebar-header">
        <div className="forge-sidebar-title">Aria Workspaces</div>
        <button className="forge-sidebar-plus" onClick={onCreate}
                data-testid="forge-workspace-new" title="Nuovo workspace">
          <Icon.plus width={14} height={14} />
        </button>
      </div>

      <div className="forge-workspace-list">
        {workspaces.map((w) => (
          <button
            key={w.id}
            onClick={() => onSelect(w.id)}
            className={`forge-workspace-item ${activeId === w.id ? "active" : ""}`}
            data-testid={`forge-workspace-item-${w.id}`}
            style={{ "--ws-color": w.color || "#8b5cf6" }}
          >
            <span className="forge-workspace-icon">
              {(Icon[w.icon] || Icon.layers)({ width: 14, height: 14 })}
            </span>
            <span className="forge-workspace-name">{w.name}</span>
            <span className="forge-workspace-count">{(w.tasks || []).length}</span>
          </button>
        ))}
      </div>

      <div className="forge-sidebar-section">Navigazione</div>
      <nav className="forge-sidebar-nav">
        <a href="/universe" data-testid="forge-nav-universe">
          <Icon.compass width={13} height={13}/> Universe
        </a>
        <a href="/api/forge-studio/" data-testid="forge-nav-console">
          <Icon.terminal width={13} height={13}/> Console
        </a>
        <a href="/providers" data-testid="forge-nav-providers">
          <Icon.sparkles width={13} height={13}/> Providers
        </a>
        <a href="/bench" data-testid="forge-nav-bench">
          <Icon.bench width={13} height={13}/> Bench
        </a>
        <a href="/memory" data-testid="forge-nav-memory">
          <Icon.file width={13} height={13}/> Memory
        </a>
      </nav>

      <div className="forge-sidebar-section">Forge Status</div>
      <div className="forge-status-list">
        <div className="forge-status-row"><span className="dot ok"/>Sistema OK</div>
        <div className="forge-status-row"><span className="dot ok"/>Aria attiva</div>
        <div className="forge-status-row"><span className="dot ok"/>Memory sync</div>
      </div>
    </aside>
  );
}

// ══════════════════════════════════════════════════════════
// CENTER — Universe preview + Task Cards grid
// ══════════════════════════════════════════════════════════
function UniversePanel({ scope, onExplain }) {
  return (
    <section className="forge-universe-panel" data-testid="forge-universe-panel">
      <div className="forge-panel-header">
        <div className="forge-panel-title">
          <span className="forge-panel-eye" />
          Project Universe
          <span className="forge-panel-sub">{scope ? `scope: ${scope}` : "vista completa"}</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="forge-panel-link ghost" onClick={() => onExplain?.("universe")}
                  data-testid="forge-universe-explain" title="Perché esiste questo pannello?">?</button>
          <a href="/universe" className="forge-panel-link" data-testid="forge-universe-open">
            Apri il mondo →
          </a>
        </div>
      </div>

      <div className="forge-universe-canvas">
        {/* Galaxy background with pulsing planets — pure CSS to keep home light */}
        <div className="forge-galaxy">
          {Array.from({ length: 40 }).map((_, i) => (
            <span key={i} className="forge-star" style={{
              left: `${(i * 37) % 100}%`,
              top:  `${(i * 53) % 100}%`,
              animationDelay: `${(i % 7) * 0.6}s`,
              opacity: 0.3 + ((i % 5) / 10),
            }}/>
          ))}
          <div className="forge-planet forge-planet-1" title="Kernel" />
          <div className="forge-planet forge-planet-2" title="Aria" />
          <div className="forge-planet forge-planet-3" title="Memory" />
          <div className="forge-planet forge-planet-4" title="Providers" />
          <div className="forge-orbit" />
        </div>
      </div>
    </section>
  );
}

function TaskCard({ workspaceId, task, onOpen }) {
  const s = STATUS[task.status] || STATUS.queued;
  return (
    <article
      className="forge-taskcard"
      data-testid={`forge-taskcard-${task.id}`}
      style={{ "--tc-color": s.color }}
      onClick={() => onOpen(task)}
    >
      <div className="forge-taskcard-head">
        <StatusPill status={task.status} />
        <span className="forge-taskcard-time">
          {(task.duration_s || 0) > 0
            ? `${Math.floor((task.duration_s || 0) / 60)}m ${(task.duration_s || 0) % 60}s`
            : "—"}
        </span>
      </div>
      <div className="forge-taskcard-title">{task.title}</div>
      <div className="forge-taskcard-files">
        {(task.files_touched || []).slice(0, 2).map((f, i) => (
          <span key={i} className="forge-taskcard-file"><Icon.file width={10} height={10}/> {f.split("/").pop()}</span>
        ))}
        {(task.files_touched || []).length > 2 && (
          <span className="forge-taskcard-file muted">+{task.files_touched.length - 2}</span>
        )}
      </div>
      <div className="forge-taskcard-progress">
        <div className="forge-taskcard-progressfill" style={{ width: `${task.progress || 0}%` }}/>
      </div>
      <div className="forge-taskcard-footer">
        <span className="forge-taskcard-explain-link">Perché · Chi · Impatto →</span>
      </div>
    </article>
  );
}

function TaskCardGrid({ workspaceId, tasks, onOpen, onNewTask, onExplain }) {
  const recent = (tasks || []).slice(-4).reverse();
  const placeholders = Math.max(0, 4 - recent.length);
  return (
    <section className="forge-taskcards" data-testid="forge-taskcards">
      <div className="forge-panel-header">
        <div className="forge-panel-title">
          <span className="forge-panel-eye" />
          Task Card recenti
          <span className="forge-panel-sub">{tasks.length} totali · workspace attivo</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="forge-panel-link ghost" onClick={() => onExplain?.("taskcards")}
                  data-testid="forge-taskcards-explain" title="Perché queste card?">?</button>
          <button className="forge-panel-link" onClick={onNewTask} data-testid="forge-task-new">+ Nuova task</button>
        </div>
      </div>
      <div className="forge-taskcards-grid">
        {recent.map((t) => (
          <TaskCard key={t.id} workspaceId={workspaceId} task={t} onOpen={onOpen}/>
        ))}
        {Array.from({ length: placeholders }).map((_, i) => (
          <div key={`ph-${i}`} className="forge-taskcard forge-taskcard-empty">
            <span className="forge-taskcard-empty-text">
              slot libero · Aria aprirà qui il prossimo lavoro
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════
// RIGHT — Aria panel (ologramma + comandi + memory insight)
// ══════════════════════════════════════════════════════════
function AriaPanel({ activeWorkspace, onCommand }) {
  const [msg, setMsg] = useState("");
  const send = () => {
    if (!msg.trim()) return;
    onCommand?.(msg.trim());
    setMsg("");
  };
  return (
    <aside className="forge-aria-panel" data-testid="forge-aria-panel">
      <div className="forge-panel-header">
        <div className="forge-panel-title">
          <span className="forge-panel-eye" />
          Aria
        </div>
        <span className="forge-panel-sub">
          {activeWorkspace ? `su «${activeWorkspace.name}»` : "in ascolto"}
        </span>
      </div>

      <div className="forge-hologram-wrap" data-testid="forge-hologram-wrap">
        <AriaHologram size={200} />
      </div>

      <div className="forge-aria-greeting">
        Ciao Andrea. Sono qui, in questo workspace.
        Cosa vuoi che apra?
      </div>

      <div className="forge-aria-suggestions">
        <button onClick={() => onCommand?.("Apri Providers")} data-testid="aria-cmd-providers">
          → Apri AI Providers
        </button>
        <button onClick={() => onCommand?.("Mostra Universe")} data-testid="aria-cmd-universe">
          → Mostra Universe
        </button>
        <button onClick={() => onCommand?.("Portami in Memory")} data-testid="aria-cmd-memory">
          → Portami in Memory
        </button>
        <button onClick={() => onCommand?.("Nuova chiave provider")} data-testid="aria-cmd-newkey">
          → Nuova key provider
        </button>
      </div>

      <div className="forge-aria-input">
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder="Descrivi un obiettivo, Aria costruisce il percorso…"
          rows={2}
          data-testid="aria-textarea"
        />
        <button onClick={send} data-testid="aria-send" aria-label="Invia">
          <Icon.send width={16} height={16}/>
        </button>
      </div>
    </aside>
  );
}

function MemoryInsight({ workspace, onLessonCreated }) {
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(null);
  const doneCount = (workspace?.tasks || []).filter(t => t.status === "done").length;
  const hint = workspace
    ? `Ho notato ${doneCount} task completate in «${workspace.name}». Vuoi che le sintetizzi in una lezione appresa?`
    : "Ho notato pattern ricorrenti tra i tuoi workspace. Vuoi che li aggreghi in una lezione?";

  const create = async () => {
    if (!workspace || busy) return;
    setBusy(true);
    try {
      const summary = `${doneCount} task completate in «${workspace.name}» (scope: ${workspace.universe_scope || "?"}).`;
      const done = (workspace.tasks || []).filter(t => t.status === "done");
      const evidence = done.map(t => t.title).slice(0, 8);
      const lesson = doneCount > 0
        ? `Pattern osservato: le task in «${workspace.name}» convergono ${doneCount === done.length ? "sempre" : "spesso"} verso completamento. File più toccati: ${
            Array.from(new Set(done.flatMap(t => (t.files_touched || []).map(f => f.split("/").pop())))).slice(0, 5).join(", ") || "n/d"
          }.`
        : `Nessun completamento osservabile in «${workspace.name}» — Aria propone di scomporre in task più piccole.`;
      const r = await fetch(`${"" || ""}/api/kernel/aria/experiences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "workspace_reflection",
          outcome: doneCount > 0 ? "success" : "neutral",
          summary, lesson, evidence,
          workspace_id: workspace.id,
        }),
      });
      const j = await r.json();
      if (j?.ok) {
        setAck("Lezione salvata. Aria la userà per proposte future.");
        onLessonCreated?.(j.experience);
      } else {
        setAck("Errore nel salvare la lezione.");
      }
    } catch {
      setAck("Aria non raggiunge la memoria esperienziale.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="forge-memory-insight" data-testid="forge-memory-insight">
      <div className="forge-memory-title">
        <Icon.sparkles width={14} height={14}/> Memory Insight
      </div>
      <div className="forge-memory-hint">{ack || hint}</div>
      <div className="forge-memory-actions">
        <button data-testid="memory-crea" onClick={create} disabled={busy || !workspace}>
          {busy ? "Sto scrivendo…" : "Crea lezione"}
        </button>
        <button className="ghost" data-testid="memory-ignora" onClick={() => setAck(null)}>
          Ignora
        </button>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════
// FOOTER — Time Machine + System Metrics
// ══════════════════════════════════════════════════════════
function TimeMachineStrip({ workspace }) {
  const events = (workspace?.timeline || []).slice(-22);
  return (
    <section className="forge-timemachine" data-testid="forge-timemachine">
      <div className="forge-timemachine-title">
        <Icon.clock width={13} height={13}/> Time Machine
        <span className="forge-timemachine-count">{events.length} snapshot</span>
      </div>
      <div className="forge-timemachine-track">
        {events.length === 0 && (
          <div className="forge-timemachine-empty">Nessuno snapshot ancora</div>
        )}
        {events.map((ev, i) => (
          <button
            key={ev.id || i}
            className={`forge-timemachine-dot kind-${ev.kind || "event"}`}
            title={`${ev.title || ""}\n${ev.detail || ""}`}
            data-testid={`tm-dot-${i}`}
          />
        ))}
        {events.length > 0 && <div className="forge-timemachine-cursor"/>}
      </div>
    </section>
  );
}

function SystemMetrics() {
  const [m, setM] = useState({ cpu: 0, ram: 0, disk: 0, ai: 0 });
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await fetch(`${"" || ""}/api/kernel/system/metrics`);
        if (r.ok) {
          const j = await r.json();
          if (alive) setM({
            cpu:  Math.round(j?.cpu_percent ?? 0),
            ram:  Math.round(j?.mem_percent ?? 0),
            disk: Math.round(j?.disk_percent ?? 0),
            ai:   Math.round(j?.ai_load ?? 0),
          });
        }
      } catch { /* offline: keep last metrics */ }
    };
    tick();
    const it = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(it); };
  }, []);
  return (
    <section className="forge-metrics" data-testid="forge-metrics">
      <div className="forge-metric"><span>CPU</span><b>{m.cpu}%</b></div>
      <div className="forge-metric"><span>RAM</span><b>{m.ram}%</b></div>
      <div className="forge-metric"><span>DISK</span><b>{m.disk}%</b></div>
      <div className="forge-metric"><span>AI</span><b>{m.ai}%</b></div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════
// MODALS — Explain task · New workspace · New task
// ══════════════════════════════════════════════════════════
function ExplainModal({ task, workspace, onClose }) {
  if (!task) return null;
  return (
    <div className="forge-modal-backdrop" onClick={onClose} data-testid="forge-explain-modal">
      <div className="forge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="forge-modal-head">
          <div className="forge-modal-title">{task.title}</div>
          <button className="forge-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="forge-modal-row"><b>Workspace</b> {workspace?.name || "—"}</div>
        <div className="forge-modal-row"><b>Stato</b> <StatusPill status={task.status}/></div>
        <div className="forge-modal-row"><b>Progresso</b> {task.progress || 0}%</div>
        <div className="forge-modal-row">
          <b>Perché esiste</b>
          <p className="forge-modal-explain">
            {task.explain || "(Aria non ha ancora annotato la ragione di questa task. Chiedile 'perché esiste?')"}
          </p>
        </div>
        <div className="forge-modal-row"><b>File toccati</b>
          <ul>
            {(task.files_touched || []).map((f, i) => <li key={i}><code>{f}</code></li>)}
            {(task.files_touched || []).length === 0 && <li className="muted">— nessuno —</li>}
          </ul>
        </div>
        {task.preview_url && (
          <div className="forge-modal-row"><b>Preview</b>
            <a href={task.preview_url} target="_blank" rel="noreferrer">{task.preview_url}</a>
          </div>
        )}
        <div className="forge-modal-row"><b>Impatto</b>
          <p className="forge-modal-explain">
            Se questa task fallisce, il workspace resta in stato incoerente.
            Aria propone un rollback all&apos;ultimo snapshot cognitivo.
          </p>
        </div>
      </div>
    </div>
  );
}

function TextPromptModal({ open, title, placeholder, onClose, onSubmit }) {
  const [v, setV] = useState("");
  useEffect(() => { if (!open) setV(""); }, [open]);
  if (!open) return null;
  return (
    <div className="forge-modal-backdrop" onClick={onClose}>
      <div className="forge-modal forge-modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="forge-modal-head">
          <div className="forge-modal-title">{title}</div>
          <button className="forge-modal-close" onClick={onClose}>×</button>
        </div>
        <textarea
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder={placeholder}
          rows={3}
          autoFocus
          data-testid="forge-prompt-input"
        />
        <div className="forge-modal-actions">
          <button className="ghost" onClick={onClose}>Annulla</button>
          <button
            onClick={() => { if (v.trim()) { onSubmit(v.trim()); onClose(); } }}
            data-testid="forge-prompt-submit"
          >Crea</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PANEL EXPLAIN — Manifesto Pt. 11 · Explain Everything
// ══════════════════════════════════════════════════════════
function PanelExplainModal({ panelId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!panelId) { setData(null); return; }
    setLoading(true);
    fetch(`${"" || ""}/api/kernel/aria/explain?kind=panel&id=${encodeURIComponent(panelId)}`)
      .then((r) => r.json())
      .then((j) => setData(j?.explanation || null))
      .finally(() => setLoading(false));
  }, [panelId]);
  if (!panelId) return null;
  return (
    <div className="forge-modal-backdrop" onClick={onClose} data-testid="forge-panel-explain-modal">
      <div className="forge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="forge-modal-head">
          <div className="forge-modal-title">Perché esiste questo pannello?</div>
          <button className="forge-modal-close" onClick={onClose}>×</button>
        </div>
        {loading && <div className="forge-modal-row muted">Aria sta ragionando…</div>}
        {data && (
          <>
            <div className="forge-modal-row"><b>Cos&apos;è</b><p className="forge-modal-explain">{data.what}</p></div>
            <div className="forge-modal-row"><b>Chi lo usa</b><p className="forge-modal-explain">{data.who}</p></div>
            <div className="forge-modal-row"><b>Perché esiste</b><p className="forge-modal-explain">{data.why}</p></div>
            <div className="forge-modal-row"><b>Cosa succede se sparisce</b><p className="forge-modal-explain">{data.impact}</p></div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PAGE
// ══════════════════════════════════════════════════════════
export default function ForgeHome() {
  const { workspaces, loading, refresh } = useWorkspaces();
  const [activeId, setActiveId] = useState(null);
  const [explainTask, setExplainTask] = useState(null);
  const [explainPanel, setExplainPanel] = useState(null);
  const [newWsOpen, setNewWsOpen] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [ariaFeedback, setAriaFeedback] = useState("");

  useEffect(() => {
    if (!activeId && workspaces.length > 0) setActiveId(workspaces[0].id);
  }, [workspaces, activeId]);

  const active = useMemo(
    () => workspaces.find((w) => w.id === activeId) || null,
    [workspaces, activeId],
  );

  // Send state changes to Aria state bus so hologram reacts
  const notifyAria = async (state, attention) => {
    try {
      await fetch(`${"" || ""}/api/kernel/aria/state/set`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ state, attention_target: attention }),
      });
    } catch { /* silent */ }
  };

  const onSelectWorkspace = async (id) => {
    setActiveId(id);
    notifyAria("navigation", "workspaces");
  };

  const onCreateWorkspace = async (name) => {
    notifyAria("working", "workspaces");
    await apiWorkspace.create({ name });
    await refresh();
    notifyAria("success", "workspaces");
  };

  const onCreateTask = async (title) => {
    if (!active) return;
    notifyAria("working", "taskcards");
    await apiTask.create(active.id, { title, status: "queued", progress: 0 });
    await refresh();
    notifyAria("success", "taskcards");
  };

  const onAriaCommand = async (text) => {
    setAriaFeedback(`Aria interpreta: "${text}"`);
    notifyAria("reasoning", "aria");
    try {
      const r = await fetch(`${"" || ""}/api/kernel/aria/intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, active_workspace_id: active?.id || null }),
      });
      const j = await r.json();
      const it = j?.intent || { type: "unknown", narration: "Non ho capito." };
      setAriaFeedback(it.narration);

      if (it.type === "navigate" && it.target) {
        notifyAria("navigation", it.route_key || "generic");
        // If target is a full external route, use location; if it starts with /api/, hit it too
        setTimeout(() => { window.location.href = it.target; }, 500);
        return;
      }
      if (it.type === "create_workspace") {
        await onCreateWorkspace(it.payload.name);
        return;
      }
      if (it.type === "create_task") {
        await onCreateTask(it.payload.title);
        return;
      }
      if (it.type === "open_panel") {
        // Scroll to and highlight the panel briefly
        const el = document.querySelector(`[data-testid="forge-${it.target}"]`)
                || document.querySelector(`[data-testid="forge-${it.target === "taskcards" ? "taskcards" : it.target}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.animate([{ boxShadow: "0 0 0 rgba(34,211,238,0)" },
                      { boxShadow: "0 0 24px rgba(34,211,238,0.8)" },
                      { boxShadow: "0 0 0 rgba(34,211,238,0)" }],
                     { duration: 1400, iterations: 1 });
        }
        notifyAria("navigation", it.target);
        return;
      }
      if (it.type === "explain") {
        notifyAria("dialogue", "chat");
        return;
      }
      if (it.type === "unknown" && it.fallback?.type === "create_task" && active) {
        await onCreateTask(it.fallback.payload.title);
        setAriaFeedback(`Task «${it.fallback.payload.title}» creata (fallback interpretativo).`);
        return;
      }
    } catch (e) {
      setAriaFeedback("Non sono riuscita a interpretare — riprova.");
      notifyAria("error", "aria");
    }
  };

  return (
    <div className="forge-home" data-testid="forge-home">
      <ForgeNavBar current="/forge" />

      <div className="forge-home-grid">
        <SidebarWorkspaces
          workspaces={workspaces}
          activeId={activeId}
          onSelect={onSelectWorkspace}
          onCreate={() => setNewWsOpen(true)}
        />

        <main className="forge-home-main">
          <UniversePanel scope={active?.universe_scope} onExplain={setExplainPanel}/>
          <TaskCardGrid
            workspaceId={active?.id}
            tasks={active?.tasks || []}
            onOpen={setExplainTask}
            onNewTask={() => setNewTaskOpen(true)}
            onExplain={setExplainPanel}
          />
        </main>

        <div className="forge-home-right">
          <AriaPanel activeWorkspace={active} onCommand={onAriaCommand} />
          <MemoryInsight workspace={active} onLessonCreated={() => notifyAria("learning", "memory-insight")}/>
          {ariaFeedback && (
            <div className="forge-aria-feedback" data-testid="forge-aria-feedback">
              {ariaFeedback}
            </div>
          )}
        </div>
      </div>

      <footer className="forge-home-footer">
        <TimeMachineStrip workspace={active}/>
        <div className="forge-home-quote">
          &ldquo;Non è solo codice. È comprensione.&rdquo; — Aria
        </div>
        <SystemMetrics/>
      </footer>

      <ExplainModal
        task={explainTask}
        workspace={active}
        onClose={() => setExplainTask(null)}
      />
      <PanelExplainModal
        panelId={explainPanel}
        onClose={() => setExplainPanel(null)}
      />
      <TextPromptModal
        open={newWsOpen}
        title="Nuovo Workspace"
        placeholder="Nome workspace · es. «Cliente X · MVP»"
        onClose={() => setNewWsOpen(false)}
        onSubmit={onCreateWorkspace}
      />
      <TextPromptModal
        open={newTaskOpen}
        title={`Nuova Task${active ? ` in «${active.name}»` : ""}`}
        placeholder="Titolo task · es. «Aggiungi endpoint /api/kernel/xxx»"
        onClose={() => setNewTaskOpen(false)}
        onSubmit={onCreateTask}
      />
    </div>
  );
}
